import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

interface IFloatingMenuPresenceContextValue {
  isMenuMounted: boolean;
  register: () => void;
  unregister: () => void;
}

const FloatingMenuPresenceContext =
  createContext<IFloatingMenuPresenceContextValue>({
    isMenuMounted: false,
    register: () => {},
    unregister: () => {},
  });

export const FloatingMenuPresenceProvider: React.FC<
  React.PropsWithChildren
> = ({ children }) => {
  const [count, setCount] = useState(0);

  const register = useCallback(() => setCount(c => c + 1), []);
  const unregister = useCallback(() => setCount(c => c - 1), []);

  const value = useMemo(
    () => ({ isMenuMounted: count > 0, register, unregister }),
    [count, register, unregister]
  );

  return (
    <FloatingMenuPresenceContext.Provider value={value}>
      {children}
    </FloatingMenuPresenceContext.Provider>
  );
};

export const useIsFloatingMenuMounted = (): boolean => {
  return useContext(FloatingMenuPresenceContext).isMenuMounted;
};

export const useRegisterFloatingMenu = (): void => {
  const { register, unregister } = useContext(FloatingMenuPresenceContext);

  useEffect(() => {
    register();
    return () => unregister();
  }, [register, unregister]);
};
